import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Banner, BlockStack, Box, Button, ButtonGroup, Checkbox, Divider, FormLayout, InlineStack, Modal, Select, Text, TextField } from "@shopify/polaris";

import { S3ImageUploader } from "../../../shared/components/s3_image_display/s3ImageUploader";

function authHeaders() {
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  return new Headers({ Authorization: `Bearer ${currentUser.token || ""}`, "Content-Type": "application/json" });
}

async function packagingRequest(url, { method = "GET", body } = {}) {
  const response = await fetch(url, {
    method,
    headers: authHeaders(),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.message || "Packaging request failed.");
  return payload;
}

const emptyCustomPackage = {
  name: "",
  package_type: "BOX",
  length_cm: "",
  width_cm: "",
  height_cm: "",
  weight_kg: "",
  save_as_material: false,
  image_s3_object_keys: "[]",
};

const packageTypeOptions = [
  { label: "Box", value: "BOX" },
  { label: "Pallet", value: "PALLET" },
];

function toEditablePackage(orderPackage) {
  return {
    ...orderPackage,
    name: orderPackage.name || "",
    package_type: orderPackage.package_type || "BOX",
    length_cm: String(orderPackage.length_cm ?? ""),
    width_cm: String(orderPackage.width_cm ?? ""),
    height_cm: String(orderPackage.height_cm ?? ""),
    weight_kg: String(orderPackage.weight_kg ?? ""),
  };
}

function packageComplete(orderPackage) {
  return [orderPackage.length_cm, orderPackage.width_cm, orderPackage.height_cm, orderPackage.weight_kg].every(
    (value) => Number.isFinite(Number(value)) && Number(value) > 0
  );
}

function firstImageKey(value) {
  try {
    const keys = typeof value === "string" ? JSON.parse(value) : value;
    return Array.isArray(keys) ? keys[0] : null;
  } catch (_error) {
    return null;
  }
}

function MaterialVisual({ material, imageUrl }) {
  if (imageUrl) {
    return <img src={imageUrl} alt={material.name} style={{ width: "100%", height: 96, objectFit: "cover", display: "block" }} />;
  }

  return (
    <div style={{ height: 96, display: "grid", placeItems: "center", background: "#f1f2f4", color: "#687284", fontSize: 38 }}>
      {material.package_type === "PALLET" ? "▤" : "□"}
    </div>
  );
}

const OrderPackagingBox = forwardRef(function OrderPackagingBox({ orderId, packages = [], onChanged }, ref) {
  const [managerOpen, setManagerOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [materialImageUrls, setMaterialImageUrls] = useState({});
  const [editablePackages, setEditablePackages] = useState([]);
  const [customPackage, setCustomPackage] = useState(emptyCustomPackage);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);

  function openManager() {
    setError(null);
    setManagerOpen(true);
  }

  useImperativeHandle(ref, () => ({
    scrollIntoView: () => {
      openManager();
    },
  }));

  useEffect(() => {
    setEditablePackages((Array.isArray(packages) ? packages : []).map(toEditablePackage));
  }, [packages]);

  async function loadMaterials() {
    try {
      const nextMaterials = await packagingRequest("/api/order-packaging/materials");
      setMaterials(nextMaterials);

      const keys = [...new Set(nextMaterials.map((material) => firstImageKey(material.image_s3_object_keys)).filter(Boolean))];
      if (keys.length === 0) {
        setMaterialImageUrls({});
      } else {
        const images = await packagingRequest("/api/aws/s3/download", { method: "POST", body: { keys } });
        setMaterialImageUrls(Object.fromEntries(images.map((image) => [image.key, image.url])));
      }
      setError(null);
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  useEffect(() => {
    loadMaterials();
  }, []);

  async function finishChange({ refreshMaterials = false } = {}) {
    if (refreshMaterials) await loadMaterials();
    await onChanged?.();
  }

  async function addMaterial(material) {
    setBusyKey(`material-${material.id}`);
    setError(null);
    try {
      await packagingRequest(`/api/order-packaging/sales-order/${orderId}`, {
        method: "POST",
        body: { packaging_material_id: material.id },
      });
      await finishChange();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyKey(null);
    }
  }

  function updateOrderPackage(packageId, field, value) {
    setEditablePackages((current) => current.map((item) => (item.id === packageId ? { ...item, [field]: value } : item)));
  }

  async function saveOrderPackage(orderPackage) {
    setBusyKey(`package-${orderPackage.id}`);
    setError(null);
    try {
      await packagingRequest(`/api/order-packaging/sales-order/${orderId}/${orderPackage.id}`, { method: "PATCH", body: orderPackage });
      await finishChange();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyKey(null);
    }
  }

  async function removeOrderPackage(orderPackage) {
    setBusyKey(`package-${orderPackage.id}`);
    setError(null);
    try {
      await packagingRequest(`/api/order-packaging/sales-order/${orderId}/${orderPackage.id}`, { method: "DELETE" });
      await finishChange();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyKey(null);
    }
  }

  function openCustomPackage() {
    setManagerOpen(false);
    setCustomOpen(true);
  }

  function closeCustomPackage() {
    setCustomOpen(false);
    setManagerOpen(true);
  }

  async function addCustomPackage() {
    setBusyKey("custom");
    setError(null);
    try {
      await packagingRequest(`/api/order-packaging/sales-order/${orderId}`, { method: "POST", body: customPackage });
      const refreshMaterials = customPackage.save_as_material;
      setCustomPackage(emptyCustomPackage);
      setCustomOpen(false);
      setManagerOpen(true);
      await finishChange({ refreshMaterials });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyKey(null);
    }
  }

  const managerContent = (
    <BlockStack gap="400">
      {error && <Banner tone="critical" title="Packaging could not be updated"><p>{error}</p></Banner>}

      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="end">
          <BlockStack gap="050">
            <Text variant="headingSm" as="h3">Choose packaging</Text>
            <Text tone="subdued">Click a box or pallet to add one to this order.</Text>
          </BlockStack>
          <Button onClick={openCustomPackage}>Custom box or pallet</Button>
        </InlineStack>

        {materials.length === 0 ? (
          <Banner tone="info" title="No reusable packaging saved yet">
            <p>Create a custom box or pallet and save it for reuse. It will appear here next time.</p>
          </Banner>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 12 }}>
            {materials.map((material) => {
              const imageKey = firstImageKey(material.image_s3_object_keys);
              return (
                <button
                  key={material.id}
                  type="button"
                  disabled={busyKey !== null}
                  onClick={() => addMaterial(material)}
                  style={{ padding: 0, overflow: "hidden", textAlign: "left", border: "1px solid #c9cccf", borderRadius: 12, background: "white", cursor: "pointer" }}
                >
                  <MaterialVisual material={material} imageUrl={materialImageUrls[imageKey]} />
                  <div style={{ padding: 12 }}>
                    <Text fontWeight="semibold" as="p">{material.name}</Text>
                    <Text tone="subdued" as="p">{`${material.length_cm} × ${material.width_cm} × ${material.height_cm} cm`}</Text>
                    <Box paddingBlockStart="150"><Text tone="success" fontWeight="semibold">{busyKey === `material-${material.id}` ? "Adding…" : "+ Add to order"}</Text></Box>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </BlockStack>

      <Divider />

      <BlockStack gap="300">
        <Text variant="headingSm" as="h3">This order</Text>

        {editablePackages.length === 0 ? (
          <div style={{ border: "1px dashed #b7bbc0", borderRadius: 12, padding: 24, textAlign: "center" }}>
            <Text fontWeight="semibold" as="p">No packaging selected</Text>
            <Text tone="subdued" as="p">Choose one of the visual tiles above.</Text>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 16 }}>
            {editablePackages.map((orderPackage, index) => {
              const ready = packageComplete(orderPackage);
              const sourceMaterial = materials.find((material) => Number(material.id) === Number(orderPackage.packaging_material_id));
              const imageKey = firstImageKey(sourceMaterial?.image_s3_object_keys);
              const imageUrl = materialImageUrls[imageKey];
              const reusable = orderPackage.packaging_material_id !== null && orderPackage.packaging_material_id !== undefined;

              return (
            <div
              key={orderPackage.id}
              style={{
                display: "grid",
                gridTemplateColumns: "96px minmax(0, 1fr)",
                gap: 16,
                border: `1px solid ${ready ? "#b7dfc2" : "#edc879"}`,
                borderRadius: 12,
                padding: 14,
                background: ready ? "#fbfefc" : "#fffdf8",
              }}
            >
              <div>
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={orderPackage.name}
                    style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 10, border: "1px solid #dfe3e8", display: "block" }}
                  />
                ) : (
                  <div style={{ width: 96, height: 96, borderRadius: 10, background: "#eef0f2", display: "grid", placeItems: "center", color: "#6d7175", fontSize: 34 }}>
                    {orderPackage.package_type === "PALLET" ? "▤" : "□"}
                  </div>
                )}
              </div>

              <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <Text fontWeight="semibold">{`Package ${index + 1} · ${orderPackage.name}`}</Text>
                    <span
                      style={{
                        flexShrink: 0,
                        borderRadius: 999,
                        padding: "4px 9px",
                        background: ready ? "#dff3e5" : "#fff0cf",
                        color: ready ? "#21643a" : "#7a4f01",
                        fontSize: 12,
                        fontWeight: 600,
                        lineHeight: 1.2,
                      }}
                    >
                      {ready ? "Ready" : "Add weight"}
                    </span>
                  </div>

                  {reusable ? (
                    <>
                      <Text tone="subdued" as="p">
                        {`${orderPackage.package_type === "PALLET" ? "Pallet" : "Box"} · fixed footprint ${orderPackage.length_cm} × ${orderPackage.width_cm} cm`}
                      </Text>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(140px, 220px))", gap: 12, paddingTop: 2 }}>
                        <TextField label="Height (cm)" type="number" min={0.01} step="0.01" value={orderPackage.height_cm} onChange={(value) => updateOrderPackage(orderPackage.id, "height_cm", value)} autoComplete="off" />
                        <TextField label="Weight (kg)" type="number" min={0.001} step="0.001" value={orderPackage.weight_kg} onChange={(value) => updateOrderPackage(orderPackage.id, "weight_kg", value)} autoComplete="off" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "minmax(180px, 2fr) minmax(130px, 1fr)", columnGap: 12, rowGap: 16 }}>
                        <TextField label="Name" value={orderPackage.name} onChange={(value) => updateOrderPackage(orderPackage.id, "name", value)} autoComplete="off" />
                        <Select label="Type" options={packageTypeOptions} value={orderPackage.package_type} onChange={(value) => updateOrderPackage(orderPackage.id, "package_type", value)} />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(90px, 1fr))", columnGap: 12, rowGap: 16, paddingTop: 2 }}>
                        <TextField label="L (cm)" type="number" min={0.01} step="0.01" value={orderPackage.length_cm} onChange={(value) => updateOrderPackage(orderPackage.id, "length_cm", value)} autoComplete="off" />
                        <TextField label="W (cm)" type="number" min={0.01} step="0.01" value={orderPackage.width_cm} onChange={(value) => updateOrderPackage(orderPackage.id, "width_cm", value)} autoComplete="off" />
                        <TextField label="H (cm)" type="number" min={0.01} step="0.01" value={orderPackage.height_cm} onChange={(value) => updateOrderPackage(orderPackage.id, "height_cm", value)} autoComplete="off" />
                        <TextField label="Weight (kg)" type="number" min={0.001} step="0.001" value={orderPackage.weight_kg} onChange={(value) => updateOrderPackage(orderPackage.id, "weight_kg", value)} autoComplete="off" />
                      </div>
                    </>
                  )}

                  <ButtonGroup>
                    <Button variant="primary" size="slim" onClick={() => saveOrderPackage(orderPackage)} loading={busyKey === `package-${orderPackage.id}`}>Save</Button>
                    <Button tone="critical" size="slim" variant="tertiary" onClick={() => removeOrderPackage(orderPackage)} disabled={busyKey !== null}>Remove</Button>
                  </ButtonGroup>
              </div>
            </div>
              );
            })}
          </div>
        )}
      </BlockStack>
    </BlockStack>
  );

  return (
    <div id="order-packaging">
      <Modal open={managerOpen} onClose={() => setManagerOpen(false)} title="Packaging for this order" size="large" secondaryActions={[{ content: "Close", onAction: () => setManagerOpen(false) }]}>
        <Modal.Section>{managerContent}</Modal.Section>
      </Modal>

      <Modal
        open={customOpen}
        onClose={closeCustomPackage}
        title="Add custom packaging"
        primaryAction={{ content: "Add to order", onAction: addCustomPackage, loading: busyKey === "custom" }}
        secondaryActions={[{ content: "Back", onAction: closeCustomPackage }]}
      >
        <Modal.Section>
          <FormLayout>
            {error && <Banner tone="critical"><p>{error}</p></Banner>}
            <FormLayout.Group>
              <TextField label="Name" value={customPackage.name} onChange={(value) => setCustomPackage((current) => ({ ...current, name: value }))} autoComplete="off" />
              <Select label="Type" options={packageTypeOptions} value={customPackage.package_type} onChange={(value) => setCustomPackage((current) => ({ ...current, package_type: value }))} />
            </FormLayout.Group>
            <FormLayout.Group condensed>
              <TextField label="Length (cm)" type="number" min={0.01} step="0.01" value={customPackage.length_cm} onChange={(value) => setCustomPackage((current) => ({ ...current, length_cm: value }))} autoComplete="off" />
              <TextField label="Width (cm)" type="number" min={0.01} step="0.01" value={customPackage.width_cm} onChange={(value) => setCustomPackage((current) => ({ ...current, width_cm: value }))} autoComplete="off" />
              <TextField label="Height (cm)" type="number" min={0.01} step="0.01" value={customPackage.height_cm} onChange={(value) => setCustomPackage((current) => ({ ...current, height_cm: value }))} autoComplete="off" />
            </FormLayout.Group>
            <TextField label="Weight (kg)" type="number" min={0.001} step="0.001" value={customPackage.weight_kg} onChange={(value) => setCustomPackage((current) => ({ ...current, weight_kg: value }))} autoComplete="off" />
            <Checkbox label="Save this packaging for easy reuse" checked={customPackage.save_as_material} onChange={(value) => setCustomPackage((current) => ({ ...current, save_as_material: value }))} />
            {customPackage.save_as_material && (
              <S3ImageUploader
                S3ObjectKeys={customPackage.image_s3_object_keys}
                S3ObjectNamePrefix="packaging_material"
                onUploadImages={(keys) => setCustomPackage((current) => ({ ...current, image_s3_object_keys: JSON.stringify(keys) }))}
                onRemoveImages={(keys) => setCustomPackage((current) => ({ ...current, image_s3_object_keys: JSON.stringify(keys) }))}
              />
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>

    </div>
  );
});

export default OrderPackagingBox;
